import { hasLineOfSight } from './gameplay.js';

export const STYLES = Object.freeze({
  human: Object.freeze({ name: 'Lantern Discipline', technique: 'Dawnseal', color: 0xe8bb69, range: 5,
    damage: 24, cooldown: 10, stamina: 28, blood: 0, stagger: 1.3, cap: 5,
    description: 'Seal nearby foes in lantern light; interrupt their windup. Costs 28 stamina.' }),
  vampire: Object.freeze({ name: 'Crimson Covenant', technique: 'Sanguine Thread', color: 0xef6b72, range: 8,
    damage: 42, cooldown: 8, stamina: 12, blood: 20, stagger: 0.45, cap: 1, quest: 'lantern-in-the-mist',
    description: 'Bind one foe ahead and recover health from damage dealt. Costs 20 blood / 12 stamina.' }),
  werewolf: Object.freeze({ name: 'Ashfang Instinct', technique: 'Crescent Rend', color: 0x9dbbdf, range: 4.5,
    damage: 54, cooldown: 12, stamina: 38, blood: 0, stagger: 0.8, cap: 3, quest: 'scars-in-greyfen',
    description: 'Cleave a frontal arc, breaking nearby attackers. Costs 38 stamina.' }),
});

const finite = (value, fallback = 0) => Number.isFinite(value) ? value : fallback;
const bound = (value, maximum) => Math.max(0, Math.min(maximum, finite(value)));

export function createAbilityState() {
  return { cooldowns: { human: 0, vampire: 0, werewolf: 0, ultimate: 0 }, resonance: 0, lastAction: null, idle: 0 };
}

export function abilityProfile(form, campaign = {}) {
  const style = STYLES[form] || STYLES.human;
  const completed = new Set(campaign.completedQuestIds || []);
  const rank = completed.size >= 6 ? 3 : completed.size >= 3 ? 2 : 1;
  return { ...style, rank, unlocked: !style.quest || completed.has(style.quest),
    damage: Math.round(style.damage * (1 + (rank - 1) * 0.2)),
    cooldown: style.cooldown * (1 - (rank - 1) * 0.08) };
}

export function recordStyleAction(state, action) {
  if (action === 'hurt') {
    state.resonance = bound(state.resonance - 18, 100);
    state.lastAction = null;
    return;
  }
  if (!['light', 'heavy', 'parry', 'technique'].includes(action)) return;
  const gain = action === 'parry' ? 22 : action === state.lastAction ? 3 : action === 'technique' ? 10 : 12;
  state.resonance = bound(state.resonance + gain, 100);
  state.lastAction = action;
  state.idle = 0;
}

export function tickAbilities(state, dt) {
  const delta = bound(dt, 0.25);
  for (const id of Object.keys(state.cooldowns)) state.cooldowns[id] = Math.max(0, finite(state.cooldowns[id]) - delta);
  state.idle = finite(state.idle) + delta;
  if (state.idle > 6) state.resonance = bound(state.resonance - delta * 5, 100);
}

export function planAbility(state, form, campaign, resources, ultimate = false) {
  if (!STYLES[form]) return { ok: false, reason: 'Unknown combat style.' };
  const profile = abilityProfile(form, campaign);
  const id = ultimate ? 'ultimate' : form;
  if (ultimate ? new Set(campaign?.completedQuestIds || []).size < 3 : !profile.unlocked) {
    return { ok: false, reason: ultimate ? 'Moonbreak unlocks after three completed quests.' : `Complete ${profile.quest} to awaken this technique.` };
  }
  if (state.cooldowns[id] > 0) return { ok: false, reason: `${Math.ceil(state.cooldowns[id])}s until ready.` };
  if (ultimate && state.resonance < 100) return { ok: false, reason: 'Moonbreak needs 100 resonance. Mix strikes and perfect parries.' };
  const stamina = ultimate ? 20 : profile.stamina;
  const blood = ultimate ? 10 : profile.blood;
  if (finite(resources.stamina) < stamina || finite(resources.blood) < blood) return { ok: false, reason: `Need ${stamina} stamina and ${blood} blood.` };
  return { ok: true, id, stamina, blood, profile: ultimate ? { ...profile, technique: 'Moonbreak', damage: 86 + profile.rank * 12,
    range: 7, cap: 8, stagger: 1.7, cooldown: 30 } : profile };
}

export function selectAbilityTargets(profile, origin, facing, targets, blockers = [], radial = false) {
  if (![origin?.x, origin?.z, facing].every(Number.isFinite)) return [];
  return targets.filter((target) => {
    if (target.dead || ![target.x, target.z].every(Number.isFinite)) return false;
    const dx = target.x - origin.x;
    const dz = target.z - origin.z;
    const distance = Math.hypot(dx, dz);
    if (distance > profile.range || !hasLineOfSight(origin, target, blockers)) return false;
    return radial || distance < 0.01 || (dx * Math.sin(facing) + dz * Math.cos(facing)) / distance >= 0.25;
  }).sort((a, b) => Math.hypot(a.x - origin.x, a.z - origin.z) - Math.hypot(b.x - origin.x, b.z - origin.z)).slice(0, profile.cap);
}
